import React from "react";
// Imported by module rather than through the `ui` barrel: the barrel reaches
// PageLayout → sidebar → auth, which uses Vite's `import.meta` and cannot be
// loaded under jest. Every tested component here does the same.
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../ui/Table";
import { TableSkeleton } from "../../../ui/TableSkeleton";
import { EmptyState } from "../../../ui/EmptyState";
import { Badge } from "../../../ui/Badge";
import { Tag } from "../../../ui/Tag";
import { HandThumbUpIcon, HandThumbDownIcon } from "@heroicons/react/20/solid";
import { formatTagValue } from "../../tag-aliases/formatTagValue";
import { useTagAliases } from "../../tag-aliases/context";
import { getScoreBadgeVariant } from "../hooks/useEvaluationData";
import type { TraceEvaluation, TraceItem } from "../types";

interface TracesTableProps {
  traces: TraceItem[];
  loading: boolean;
  selectedId: string | null;
  onRowClick: (id: string) => void;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date}, ${time}`;
}

function extractContent(content: unknown): string {
  if (!content || typeof content !== "string") return "—";
  return content.length > 20 ? content.slice(0, 20) + "…" : content;
}

function extractPreview(trace: Record<string, any> | null): string {
  if (!trace) return "—";
  try {
    // MultiAgentTrace schema (/trace endpoint): { root_agent_instance_id, agent_instance_by_id }
    if (trace.root_agent_instance_id) {
      const rootId = trace.root_agent_instance_id;
      const content = trace.agent_instance_by_id?.[rootId]?.interactions?.[0]?.input_messages?.[0]?.content;
      return extractContent(content);
    }
    // Single AgentInstance schema (/reward endpoint): { id, name, interactions }
    if (Array.isArray(trace.interactions)) {
      const content = trace.interactions[0]?.input_messages?.[0]?.content;
      return extractContent(content);
    }
    return "—";
  } catch {
    return "—";
  }
}

/** Tags shown before the cell folds the rest behind the +N chip. */
const COLLAPSED_TAG_COUNT = 2;

function TraceTags({ tags }: { tags: Record<string, string> | null }) {
  // Read-only here: the cell shows two tags in a dense row, so renaming lives
  // where there is room for it — the heatmap, the legend, the filter list.
  const { aliases } = useTagAliases();
  const [expanded, setExpanded] = React.useState(false);
  const listId = React.useId();
  if (!tags) return <span className="text-muted-foreground">—</span>;
  const entries = Object.entries(tags);
  const overflow = entries.length - COLLAPSED_TAG_COUNT;
  const visible = expanded ? entries : entries.slice(0, COLLAPSED_TAG_COUNT);
  return (
    // The table lays itself out on content width, so an unbounded expanded cell
    // would widen the Tags column and reflow every other row. Capped only while
    // expanded, which leaves the collapsed table exactly as it was.
    <div id={listId} className={`flex flex-wrap gap-1 items-center ${expanded ? "max-w-sm" : ""}`}>
      {visible.map(([k, v]) => (
        <Tag
          key={k}
          label={k}
          value={formatTagValue(v, aliases)}
          title={`${k}:${v}`}
          className="px-1.5 py-0.5 font-mono whitespace-nowrap"
        />
      ))}
      {overflow > 0 && (
        <button
          type="button"
          // The chip reads as a control, so it behaves as one: it reveals the
          // tags it is counting rather than letting the click reach the row and
          // open the trace viewer (issue #1525).
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          // Same colours as a label-only `Tag`, which the visible chips beside it
          // use: `text-muted-foreground` on `bg-muted` measured 4.31:1 in the
          // light theme, under the 4.5:1 floor for small text (issue #1526).
          className="px-1.5 py-0.5 bg-muted text-foreground font-medium rounded text-xs hover:bg-muted-foreground/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-expanded={expanded}
          // The tags this reveals sit before the chip in the DOM, so a screen
          // reader that has just activated it would otherwise walk forward past
          // them. aria-controls names the list to jump back to.
          aria-controls={listId}
          aria-label={expanded ? "Show fewer tags" : `Show ${overflow} more ${overflow === 1 ? "tag" : "tags"}`}
          title={expanded ? "Show fewer tags" : `Show ${overflow} more ${overflow === 1 ? "tag" : "tags"}`}
        >
          {expanded ? "Show less" : `+${overflow}`}
        </button>
      )}
    </div>
  );
}

/**
 * Lowest-scoring evaluation on a trace, or null when none of them scored.
 *
 * A trace can carry several evaluations (one per criterion), and
 * `EvaluationStore.get_for_traces` returns them unordered with no timestamp to
 * sort by — so "the latest" isn't knowable here and "the first" would shuffle
 * between page loads. The worst score is both stable and the one worth
 * surfacing: a trace that failed one criterion has failed. Ties break on
 * evaluator name so the cell doesn't flip between identical-looking rows.
 *
 * Note this is the worst score on the whole trace, not within the active
 * filters. `Trace.list_traces` matches on EXISTS ("the trace has at least one
 * evaluation in range") and attaches every evaluation regardless, so a row
 * matched by a `score >= 0.8` filter can legitimately show 0.10 from a
 * criterion the filter never mentioned.
 */
function worstEvaluation(evaluations: TraceEvaluation[]): TraceEvaluation | null {
  const scored = evaluations.filter((e) => e.score !== null);
  if (scored.length === 0) return null;
  return scored.reduce((worst, e) => {
    if (e.score! !== worst.score!) return e.score! < worst.score! ? e : worst;
    return (e.evaluator_name ?? "") < (worst.evaluator_name ?? "") ? e : worst;
  });
}

function Score({ evaluations }: { evaluations: TraceEvaluation[] }) {
  const worst = worstEvaluation(evaluations);
  if (!worst) return <span className="text-muted-foreground">—</span>;
  // Two different counts, each honest about what it claims: the tooltip counts
  // the scores this badge is the lowest of, while "+N more" counts every other
  // evaluation the detail pane will list when the row is clicked — including
  // any that never scored.
  const scored = evaluations.filter((e) => e.score !== null).length;
  const others = evaluations.length - 1;
  const evaluator = worst.evaluator_name ? ` — ${worst.evaluator_name}` : "";
  return (
    <div className="flex items-center gap-1.5">
      <Badge
        variant={getScoreBadgeVariant(worst.score)}
        title={scored > 1 ? `Lowest of ${scored} scores${evaluator}` : (worst.evaluator_name ?? undefined)}
      >
        {worst.score!.toFixed(2)}
      </Badge>
      {others > 0 && (
        <span className="text-xs text-muted-foreground whitespace-nowrap">+{others} more</span>
      )}
    </div>
  );
}

function Annotation({ rating }: { rating: boolean | null }) {
  if (rating === true) return <HandThumbUpIcon className="w-5 h-5 text-success" />;
  if (rating === false) return <HandThumbDownIcon className="w-5 h-5 text-destructive" />;
  return <span className="text-muted-foreground">—</span>;
}

export function TracesTable({ traces, loading, selectedId, onRowClick }: TracesTableProps) {
  if (loading) {
    return <TableSkeleton columns={6} rows={10} />;
  }

  if (traces.length === 0) {
    return (
      <EmptyState
        message="No traces found"
        description="No traces match your current filters."
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableCell variant="header">Timestamp</TableCell>
        <TableCell variant="header">Trace ID</TableCell>
        <TableCell variant="header">Tags</TableCell>
        <TableCell variant="header">Trace Preview</TableCell>
        <TableCell variant="header">Score</TableCell>
        <TableCell variant="header">Annotation</TableCell>
      </TableHeader>
      <TableBody>
        {traces.map((trace) => (
          <TableRow
            key={trace.id}
            onClick={() => onRowClick(trace.id)}
            className={selectedId === trace.id ? "bg-primary/10" : ""}
          >
            <TableCell variant="body" nowrap>
              <span className="text-muted-foreground">{formatTimestamp(trace.created_at)}</span>
            </TableCell>
            <TableCell variant="body">
              <span className="font-mono text-primary" title={trace.id}>{trace.id.slice(0, 7)}</span>
            </TableCell>
            <TableCell variant="body">
              <TraceTags tags={trace.tags} />
            </TableCell>
            <TableCell variant="body">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-muted-foreground">{extractPreview(trace.trace)}</span>
                {(trace.evaluations ?? []).some((e) => e.cached) && (
                  <Badge variant="success" title="This trace reused a recently-cached prompt prefix">
                    Cached
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell variant="body" nowrap>
              <Score evaluations={trace.evaluations ?? []} />
            </TableCell>
            <TableCell variant="body">
              <Annotation rating={trace.rating} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
